import React, { useEffect, useState, useCallback } from 'react';
import {
  collection, getDocs, doc, updateDoc, query, orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth, SUPER_ADMIN_EMAIL } from '../context/AuthContext';
import AppDialog from '../components/AppDialog';

// ─── helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    pending:  { bg: '#fff8e1', color: '#f39c12', label: 'Pending' },
    approved: { bg: '#eafaf1', color: '#27ae60', label: 'Approved' },
    revoked:  { bg: '#fdf2f2', color: '#e74c3c', label: 'Revoked' },
  };
  const style = map[status] || map.pending;
  return (
    <span style={{
      background: style.bg, color: style.color,
      padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
    }}>
      {style.label}
    </span>
  );
}

function ApprovalBadge({ status }) {
  const map = {
    pending:  { bg: '#fff8e1', color: '#f39c12', label: 'Pending Approval' },
    approved: { bg: '#eafaf1', color: '#27ae60', label: 'Approved' },
    rejected: { bg: '#fdf2f2', color: '#e74c3c', label: 'Rejected' },
  };
  const style = map[status] || map.pending;
  return (
    <span style={{
      background: style.bg, color: style.color,
      padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
    }}>
      {style.label}
    </span>
  );
}

// ─── Tab: Admin Requests ──────────────────────────────────────────────────────

function AdminRequestsTab({ currentProfile }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'admin_profiles'), orderBy('created_at', 'desc')));
    setProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => !p.is_super_admin));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (profile) => {
    setActionLoading(profile.uid);
    try {
      await updateDoc(doc(db, 'admin_profiles', profile.uid), {
        status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by_uid: currentProfile.uid,
        approved_by_email: currentProfile.email,
      });
      await load();
    } finally { setActionLoading(''); }
  };

  const reject = async (profile) => {
    setActionLoading(profile.uid);
    try {
      await updateDoc(doc(db, 'admin_profiles', profile.uid), {
        status: 'revoked',
        revoked_at: new Date().toISOString(),
        revoked_by_uid: currentProfile.uid,
        revoked_by_email: currentProfile.email,
      });
      await load();
    } finally { setActionLoading(''); }
  };

  const pending = profiles.filter(p => p.status === 'pending');
  const others  = profiles.filter(p => p.status !== 'pending');

  if (loading) return <div style={s.empty}>Loading...</div>;

  return (
    <div>
      {dialog && <AppDialog {...dialog} onClose={() => setDialog(null)} />}

      <h3 style={s.sectionTitle}>Pending Requests ({pending.length})</h3>
      {pending.length === 0 ? (
        <div style={s.empty}>No pending admin requests.</div>
      ) : (
        <div style={s.table}>
          {pending.map(p => (
            <div key={p.uid} style={s.row}>
              <div style={s.rowInfo}>
                <div style={s.rowEmail}>{p.email}</div>
                <div style={s.rowMeta}>Requested: {new Date(p.created_at).toLocaleDateString()}</div>
              </div>
              <div style={s.rowActions}>
                <StatusBadge status={p.status} />
                <button
                  style={{ ...s.btn, ...s.btnSuccess }}
                  disabled={actionLoading === p.uid}
                  onClick={() => approve(p)}
                >
                  Approve
                </button>
                <button
                  style={{ ...s.btn, ...s.btnDanger }}
                  disabled={actionLoading === p.uid}
                  onClick={() => reject(p)}
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {others.length > 0 && (
        <>
          <h3 style={{ ...s.sectionTitle, marginTop: '32px' }}>Processed Requests</h3>
          <div style={s.table}>
            {others.map(p => (
              <div key={p.uid} style={s.row}>
                <div style={s.rowInfo}>
                  <div style={s.rowEmail}>{p.email}</div>
                  <div style={s.rowMeta}>Requested: {new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                <div style={s.rowActions}>
                  <StatusBadge status={p.status} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab: Manage Admins ────────────────────────────────────────────────────────

function ManageAdminsTab({ currentProfile }) {
  const { sendResetEmail } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [dialog, setDialog] = useState(null);

  const showAlert   = (title, message) => setDialog({ title, message });
  const showConfirm = (title, message, onConfirm) =>
    setDialog({ title, message, onConfirm, danger: true, confirmText: 'Confirm', cancelText: 'Cancel' });

  const load = useCallback(async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'admin_profiles'), orderBy('created_at', 'desc')));
    setProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => !p.is_super_admin));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (profile) => {
    showConfirm(
      'Revoke Access',
      `Revoke access for ${profile.email}? They will no longer be able to log in.`,
      async () => {
        setActionLoading(profile.uid);
        try {
          await updateDoc(doc(db, 'admin_profiles', profile.uid), {
            status: 'revoked',
            revoked_at: new Date().toISOString(),
            revoked_by_uid: currentProfile.uid,
            revoked_by_email: currentProfile.email,
          });
          await load();
        } finally { setActionLoading(''); }
      }
    );
  };

  const reActivate = async (profile) => {
    setActionLoading(profile.uid);
    try {
      await updateDoc(doc(db, 'admin_profiles', profile.uid), {
        status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by_uid: currentProfile.uid,
        approved_by_email: currentProfile.email,
      });
      await load();
    } finally { setActionLoading(''); }
  };

  const resetPassword = async (profile) => {
    showConfirm(
      'Reset Password',
      `Send a password reset email to ${profile.email}?`,
      async () => {
        setActionLoading(profile.uid);
        try {
          await sendResetEmail(profile.email);
          showAlert('Email Sent', `Password reset email sent to ${profile.email}.`);
        } catch (err) {
          showAlert('Error', err.message || 'Failed to send reset email');
        } finally { setActionLoading(''); }
      }
    );
  };

  const approved = profiles.filter(p => p.status === 'approved');
  const revoked  = profiles.filter(p => p.status === 'revoked');

  if (loading) return <div style={s.empty}>Loading...</div>;

  return (
    <div>
      {dialog && <AppDialog {...dialog} onClose={() => setDialog(null)} />}

      <h3 style={s.sectionTitle}>Active Admins ({approved.length})</h3>
      {approved.length === 0 ? (
        <div style={s.empty}>No active admins.</div>
      ) : (
        <div style={s.table}>
          {approved.map(p => (
            <div key={p.uid} style={s.row}>
              <div style={s.rowInfo}>
                <div style={s.rowEmail}>{p.email}</div>
                <div style={s.rowMeta}>
                  Approved: {p.approved_at ? new Date(p.approved_at).toLocaleDateString() : 'N/A'}
                  {p.approved_by_email && ` by ${p.approved_by_email}`}
                </div>
              </div>
              <div style={s.rowActions}>
                <StatusBadge status={p.status} />
                <button
                  style={{ ...s.btn, ...s.btnSecondary }}
                  disabled={actionLoading === p.uid}
                  onClick={() => resetPassword(p)}
                >
                  Reset Password
                </button>
                <button
                  style={{ ...s.btn, ...s.btnDanger }}
                  disabled={actionLoading === p.uid}
                  onClick={() => revoke(p)}
                >
                  Revoke
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {revoked.length > 0 && (
        <>
          <h3 style={{ ...s.sectionTitle, marginTop: '32px' }}>Revoked Admins ({revoked.length})</h3>
          <div style={s.table}>
            {revoked.map(p => (
              <div key={p.uid} style={s.row}>
                <div style={s.rowInfo}>
                  <div style={s.rowEmail}>{p.email}</div>
                  <div style={s.rowMeta}>Revoked: {p.revoked_at ? new Date(p.revoked_at).toLocaleDateString() : 'N/A'}</div>
                </div>
                <div style={s.rowActions}>
                  <StatusBadge status={p.status} />
                  <button
                    style={{ ...s.btn, ...s.btnSuccess }}
                    disabled={actionLoading === p.uid}
                    onClick={() => reActivate(p)}
                  >
                    Re-activate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab: All Tournaments ──────────────────────────────────────────────────────

function AllTournamentsTab({ currentProfile }) {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [dialog, setDialog] = useState(null);

  const showAlert = (title, message) => setDialog({ title, message });

  const load = useCallback(async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'tournaments'), orderBy('created_at', 'desc')));
    setTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approveT = async (t) => {
    setActionLoading(t.id);
    try {
      await updateDoc(doc(db, 'tournaments', t.id), {
        approval_status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by_uid: currentProfile.uid,
        approved_by_email: currentProfile.email,
      });
      await load();
    } catch (err) {
      showAlert('Error', err.message);
    } finally { setActionLoading(''); }
  };

  const rejectT = async (t) => {
    setActionLoading(t.id);
    try {
      await updateDoc(doc(db, 'tournaments', t.id), {
        approval_status: 'rejected',
        rejected_at: new Date().toISOString(),
        rejected_by_uid: currentProfile.uid,
        rejected_by_email: currentProfile.email,
      });
      await load();
    } catch (err) {
      showAlert('Error', err.message);
    } finally { setActionLoading(''); }
  };

  if (loading) return <div style={s.empty}>Loading...</div>;

  const pending  = tournaments.filter(t => (t.approval_status || 'pending') === 'pending');
  const approved = tournaments.filter(t => t.approval_status === 'approved');
  const rejected = tournaments.filter(t => t.approval_status === 'rejected');

  return (
    <div>
      {dialog && <AppDialog {...dialog} onClose={() => setDialog(null)} />}

      <h3 style={s.sectionTitle}>Pending Approval ({pending.length})</h3>
      {pending.length === 0 ? (
        <div style={s.empty}>No tournaments pending approval.</div>
      ) : (
        <div style={s.table}>
          {pending.map(t => (
            <div key={t.id} style={s.row}>
              <div style={s.rowInfo}>
                <div style={s.rowEmail}>{t.name}</div>
                <div style={s.rowMeta}>
                  By: {t.admin_email || 'Unknown'} &middot; {t.type} &middot; {t.format?.replace('_', ' ')}
                  {t.created_at && ` &middot; ${new Date(t.created_at).toLocaleDateString()}`}
                </div>
              </div>
              <div style={s.rowActions}>
                <ApprovalBadge status={t.approval_status || 'pending'} />
                <button
                  style={{ ...s.btn, ...s.btnSuccess }}
                  disabled={actionLoading === t.id}
                  onClick={() => approveT(t)}
                >
                  Approve
                </button>
                <button
                  style={{ ...s.btn, ...s.btnDanger }}
                  disabled={actionLoading === t.id}
                  onClick={() => rejectT(t)}
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {approved.length > 0 && (
        <>
          <h3 style={{ ...s.sectionTitle, marginTop: '32px' }}>Approved Tournaments ({approved.length})</h3>
          <div style={s.table}>
            {approved.map(t => (
              <div key={t.id} style={s.row}>
                <div style={s.rowInfo}>
                  <div style={s.rowEmail}>{t.name}</div>
                  <div style={s.rowMeta}>By: {t.admin_email || 'Unknown'} &middot; {t.type}</div>
                </div>
                <div style={s.rowActions}>
                  <ApprovalBadge status="approved" />
                  <button
                    style={{ ...s.btn, ...s.btnDanger }}
                    disabled={actionLoading === t.id}
                    onClick={() => rejectT(t)}
                  >
                    Revoke Approval
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {rejected.length > 0 && (
        <>
          <h3 style={{ ...s.sectionTitle, marginTop: '32px' }}>Rejected Tournaments ({rejected.length})</h3>
          <div style={s.table}>
            {rejected.map(t => (
              <div key={t.id} style={s.row}>
                <div style={s.rowInfo}>
                  <div style={s.rowEmail}>{t.name}</div>
                  <div style={s.rowMeta}>By: {t.admin_email || 'Unknown'} &middot; {t.type}</div>
                </div>
                <div style={s.rowActions}>
                  <ApprovalBadge status="rejected" />
                  <button
                    style={{ ...s.btn, ...s.btnSuccess }}
                    disabled={actionLoading === t.id}
                    onClick={() => approveT(t)}
                  >
                    Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab: My Account ──────────────────────────────────────────────────────────

function MyAccountTab() {
  const { admin, adminProfile, sendResetEmail, changeOwnPassword } = useAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [resetSent, setResetSent] = useState(false);

  const showAlert = (title, message) => setDialog({ title, message });

  const handleChange = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      showAlert('Mismatch', 'New passwords do not match.');
      return;
    }
    if (form.next.length < 6) {
      showAlert('Too Short', 'Password must be at least 6 characters.');
      return;
    }
    setSaving(true);
    try {
      await changeOwnPassword(form.current, form.next);
      showAlert('Success', 'Password changed successfully.');
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      showAlert('Error', err.message || 'Failed to change password. Check your current password.');
    } finally { setSaving(false); }
  };

  const handleResetEmail = async () => {
    try {
      await sendResetEmail(admin.email);
      setResetSent(true);
    } catch (err) {
      showAlert('Error', err.message || 'Failed to send reset email.');
    }
  };

  return (
    <div style={{ maxWidth: '480px' }}>
      {dialog && <AppDialog {...dialog} onClose={() => setDialog(null)} />}

      <div style={s.accountCard}>
        <div style={s.accountLabel}>Email</div>
        <div style={s.accountValue}>{admin?.email}</div>
        <div style={s.accountLabel}>Role</div>
        <div style={s.accountValue}>{adminProfile?.is_super_admin ? 'Super Admin' : 'Admin'}</div>
        <div style={s.accountLabel}>Status</div>
        <div style={s.accountValue}><StatusBadge status={adminProfile?.status} /></div>
      </div>

      <h3 style={{ ...s.sectionTitle, marginTop: '24px' }}>Change Password</h3>
      <form onSubmit={handleChange}>
        <div className="form-group">
          <label>Current Password</label>
          <input
            className="form-control"
            type="password"
            value={form.current}
            onChange={e => setForm(f => ({ ...f, current: e.target.value }))}
            required
          />
        </div>
        <div className="form-group">
          <label>New Password</label>
          <input
            className="form-control"
            type="password"
            value={form.next}
            onChange={e => setForm(f => ({ ...f, next: e.target.value }))}
            required
          />
        </div>
        <div className="form-group">
          <label>Confirm New Password</label>
          <input
            className="form-control"
            type="password"
            value={form.confirm}
            onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
            required
          />
        </div>
        <button className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Change Password'}
        </button>
      </form>

      <div style={{ marginTop: '24px', borderTop: '1px solid #eee', paddingTop: '24px' }}>
        <h3 style={s.sectionTitle}>Reset via Email</h3>
        <p style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '12px' }}>
          Send a password reset link to your email address.
        </p>
        {resetSent ? (
          <div style={{ color: '#27ae60', fontSize: '14px' }}>Reset email sent to {admin?.email}</div>
        ) : (
          <button className="btn" style={{ background: '#f0f4f8', color: '#1a2332' }} onClick={handleResetEmail}>
            Send Reset Email
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

const TABS_SUPER = [
  { key: 'requests',    label: 'Admin Requests' },
  { key: 'admins',      label: 'Manage Admins' },
  { key: 'tournaments', label: 'All Tournaments' },
  { key: 'account',     label: 'My Account' },
];
const TABS_ADMIN = [
  { key: 'account', label: 'My Account' },
];

export default function AdminManagement() {
  const { isSuperAdmin, adminProfile } = useAuth();
  const tabs = isSuperAdmin ? TABS_SUPER : TABS_ADMIN;
  const [activeTab, setActiveTab] = useState(tabs[0].key);

  return (
    <div className="container" style={{ padding: '32px 20px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1a2332', margin: 0 }}>
          {isSuperAdmin ? 'Admin Management' : 'My Account'}
        </h1>
        <p style={{ color: '#7f8c8d', marginTop: '4px', fontSize: '14px' }}>
          {isSuperAdmin
            ? 'Manage admin accounts, approve access requests, and oversee tournaments.'
            : 'Manage your account settings and password.'}
        </p>
      </div>

      <div style={s.tabBar}>
        {tabs.map(t => (
          <button
            key={t.key}
            style={{ ...s.tabBtn, ...(activeTab === t.key ? s.tabBtnActive : {}) }}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={s.content}>
        {activeTab === 'requests'    && <AdminRequestsTab currentProfile={adminProfile} />}
        {activeTab === 'admins'      && <ManageAdminsTab  currentProfile={adminProfile} />}
        {activeTab === 'tournaments' && <AllTournamentsTab currentProfile={adminProfile} />}
        {activeTab === 'account'     && <MyAccountTab />}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  tabBar: {
    display: 'flex', gap: '4px', background: '#f0f4f8',
    borderRadius: '8px', padding: '4px', marginBottom: '24px',
    flexWrap: 'wrap',
  },
  tabBtn: {
    padding: '9px 18px', borderRadius: '6px', fontSize: '14px', fontWeight: '600',
    background: 'transparent', color: '#7f8c8d', cursor: 'pointer', border: 'none', transition: 'all 0.2s',
  },
  tabBtnActive: { background: '#fff', color: '#27ae60', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  content: {
    background: '#fff', borderRadius: '12px', padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  sectionTitle: { fontSize: '15px', fontWeight: '700', color: '#1a2332', marginBottom: '12px', marginTop: 0 },
  empty: { color: '#7f8c8d', fontSize: '14px', padding: '16px 0' },
  table: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px', border: '1px solid #eef2f7', borderRadius: '8px',
    gap: '12px', flexWrap: 'wrap',
  },
  rowInfo: { flex: 1, minWidth: 0 },
  rowEmail: { fontWeight: '600', color: '#1a2332', fontSize: '14px' },
  rowMeta: { color: '#7f8c8d', fontSize: '12px', marginTop: '2px' },
  rowActions: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  btn: {
    padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', border: 'none', transition: 'opacity 0.2s',
  },
  btnSuccess:   { background: '#eafaf1', color: '#27ae60' },
  btnDanger:    { background: '#fdf2f2', color: '#e74c3c' },
  btnSecondary: { background: '#f0f4f8', color: '#1a2332' },
  accountCard: {
    background: '#f8fafc', borderRadius: '8px', padding: '16px 20px',
    display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 16px', alignItems: 'center',
  },
  accountLabel: { fontSize: '13px', color: '#7f8c8d', fontWeight: '600' },
  accountValue: { fontSize: '14px', color: '#1a2332', fontWeight: '500' },
};
